
/**
 * Supabase認証フック
 * 認証状態の管理とプロファイル情報の取得を行う
 */

import { createClient } from '@/shared/api/supabase/client'
import type { Profile } from '@/features/users/types'
import { UserRole } from '@/features/users/types'
import type { AuthChangeEvent, Session, SupabaseClient, User as SupabaseUser } from '@supabase/supabase-js'
import { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'

/**
 * アプリケーション用ユーザー型
 */
export type AppUser = SupabaseUser & Omit<Profile, 'id' | 'created_at' | 'updated_at'> & {
    /** ユーザー名 */
    username: string | null
    /** 表示名 */
    name: string | null
    /** ユーザーロール */
    role: UserRole | null
    /** アクティブ状態 */
    is_active?: boolean
    /** 職人ID */
    craftsman_id?: string | null
}

/**
 * Supabase認証コンテキストの型
 */
interface SupabaseAuthContextType {
    /** Supabaseクライアント */
    supabase: SupabaseClient
    /** セッション */
    session: Session | null
    /** ユーザー情報 */
    user: AppUser | null
    /** 管理者かどうか */
    isAdmin: boolean
    /** ローディング状態 */
    isLoading: boolean
    /** サインアウト */
    signOut: () => Promise<void>
}

const SupabaseAuthContext = createContext<SupabaseAuthContextType | undefined>(
    undefined
)

/**
 * Supabase認証プロバイダーのプロパティ
 */
interface SupabaseAuthProviderProps {
    /** 子コンポーネント */
    children: ReactNode
}

/**
 * Supabase認証プロバイダー
 */
export const SupabaseAuthProvider = ({ children }: SupabaseAuthProviderProps) => {
    const supabase = useMemo(() => createClient(), [])
    const [session, setSession] = useState<Session | null>(null)
    const [user, setUser] = useState<AppUser | null>(null)
    const [isAdmin, setIsAdmin] = useState<boolean>(false)
    const [isLoading, setIsLoading] = useState<boolean>(true)

    const logStateChange = (action: string, newState: unknown) => {
        console.log(`[SupabaseAuth] ${action}:`, newState)
    }

    const isMountedRef = useRef(true)
    const bootstrapCompletedRef = useRef(false)
    const isRefreshingRef = useRef(false)

    const userRef = useRef(user)
    useEffect(() => {
        userRef.current = user
    }, [user])





    const fetchProfile = useCallback(async (userId: string, currentSession: Session | null) => {
        if (!isMountedRef.current) return;

        try {
            console.log('[SupabaseAuth] fetchProfile started for user:', userId);

            const { default: apiClient } = await import('@/shared/api/client')
            const response = await apiClient.get('/users/me', {
                headers: {
                    'Authorization': `Bearer ${currentSession?.access_token}`,
                }
            })

            if (!isMountedRef.current) return

            const data = response.data

            const baseUser = currentSession?.user || userRef.current
            if (baseUser) {
                const appUser: AppUser = {
                    ...(baseUser as SupabaseUser),
                    id: data.id,
                    email: data.email || baseUser.email || '',
                    username: baseUser.email?.split('@')[0] || null,
                    name: baseUser.user_metadata?.name || baseUser.email || null,
                    role: data.role as UserRole,
                    is_active: true,
                    craftsman_id: data.craftsman_id || null,
                }
                setUser(appUser)
                const isAdminValue = data.role.toUpperCase() === UserRole.ADMIN
                setIsAdmin(true)
                setIsLoading(false)
                console.log('[SupabaseAuth] Profile updated with detailed info:', {
                    role: data.role,
                    roleUpper: data.role.toUpperCase(),
                    UserRoleADMIN: UserRole.ADMIN,
                    isAdmin: isAdminValue
                });
            }
        } catch (error: unknown) {
            console.error('[SupabaseAuth] fetchProfile error:', error);

            if (!isMountedRef.current) return;

            if (error instanceof Error && 'response' in error && (error as any).response?.status === 401) {
                console.log('[SupabaseAuth] 401 Unauthorized detected - APIクライアントが自動ログアウト処理する')
                setSession(null)
                setUser(null)
                setIsLoading(false)
                return
            }

            if (error instanceof Error && 'response' in error && (error as any).response?.data?.detail?.includes('User profile not found')) {
                console.error('[SupabaseAuth] User profile not found in database')
                setSession(null)
                setUser(null)
                setIsLoading(false)
                return
            }

            console.log('[SupabaseAuth] Non-401 error - setting default USER role')
            const baseUser = currentSession?.user || userRef.current
            if (baseUser) {
                const defaultAppUser: AppUser = {
                    ...(baseUser as SupabaseUser),
                    email: baseUser.email || '',
                    username: baseUser.email?.split('@')[0] || null,
                    name: baseUser.user_metadata?.name || baseUser.email || null,
                    role: UserRole.USER,
                    is_active: true,
                    craftsman_id: null,
                }
                setUser(defaultAppUser)
                setIsLoading(false)
                console.log('[SupabaseAuth] Default USER role set due to non-401 error');
            }
        }
    }, []);

    useEffect(() => {
        isMountedRef.current = true;

        const bootstrapAuth = async () => {
            if (!isMountedRef.current || bootstrapCompletedRef.current) {
                return;
            }

            console.log('[SupabaseAuth] bootstrapAuth started');
            setIsLoading(true);
            bootstrapCompletedRef.current = true;

            try {
                console.log('[SupabaseAuth] Starting getSession...')
                const startTime = Date.now()

                const timeoutPromise = new Promise((_, reject) => {
                    setTimeout(() => reject(new Error('getSession timeout after 2 seconds')), 2000)
                })

                const sessionPromise = supabase.auth.getSession()

                const result = await Promise.race([sessionPromise, timeoutPromise]) as { data: { session: Session | null }; error: Error | null }
                const { data: { session }, error: sessionError } = result
                const elapsed = Date.now() - startTime
                console.log('[SupabaseAuth] Session result:', { hasSession: !!session, error: sessionError, elapsed: `${elapsed}ms` })

                if (!isMountedRef.current) {
                    setIsLoading(false)
                    return
                }

                if (sessionError || !session?.user) {
                    console.log('[SupabaseAuth] No valid session, clearing state')
                    setSession(null)
                    logStateChange('setSession', null)
                    setUser(null)
                    logStateChange('setUser', null)
                    setIsLoading(false)
                    logStateChange('setIsLoading (no session)', false)
                    return
                }

                const now = Math.floor(Date.now() / 1000)
                if (session.expires_at && session.expires_at < now) {
                    console.log('[SupabaseAuth] Session expired, attempting refresh...')
                    try {
                        const { data: refreshData, error: refreshError } = await supabase.auth.refreshSession()
                        if (refreshError || !refreshData.session) {
                            console.log('[SupabaseAuth] Refresh failed, signing out')
                            setSession(null)
                            setUser(null)
                            setIsAdmin(false)
                            setIsLoading(false)
                            supabase.auth.signOut()
                            return
                        }
                        console.log('[SupabaseAuth] Session refreshed successfully')
                        const refreshedSession = refreshData.session
                        setSession(refreshedSession)
                    } catch (refreshError) {
                        console.error('[SupabaseAuth] Refresh error:', refreshError)
                        setSession(null)
                        setUser(null)
                        setIsAdmin(false)
                        setIsLoading(false)
                        supabase.auth.signOut()
                        return
                    }
                }

                setSession(session)

                const currentUser = userRef.current
                if (!currentUser || currentUser.id !== session.user.id) {
                    const baseUser: AppUser = {
                        ...(session.user as SupabaseUser),
                        email: session.user.email || '',
                        username: session.user.email?.split('@')[0] || null,
                        name: session.user.user_metadata?.name || session.user.email || null,
                        role: 'user' as UserRole,
                        is_active: true,
                        craftsman_id: null,
                    }
                    setUser(baseUser)
                    logStateChange('setUser (quick)', { userId: baseUser.id, role: baseUser.role })
                    console.log('[SupabaseAuth] New user set in bootstrapAuth:', { userId: session.user.id })
                } else {
                    console.log('[SupabaseAuth] Existing user info preserved in bootstrapAuth:', { userId: currentUser.id, role: currentUser.role })
                }
                setIsLoading(false)
                logStateChange('setIsLoading', false)

                console.log('[SupabaseAuth] Quick user set for immediate display:', { userId: session.user.id })

                fetchProfile(session.user.id, session).catch(error => {
                    console.error('[SupabaseAuth] Background profile fetch failed:', error)
                })
            } catch (error) {
                console.error('[SupabaseAuth] bootstrapAuth error:', error)

                if (isMountedRef.current) {
                    setSession(null)
                    logStateChange('setSession (error)', null)
                    setUser(null)
                    logStateChange('setUser (error)', null)
                    setIsLoading(false)
                    logStateChange('setIsLoading (error)', false)
                }
            } finally {
                console.log('[SupabaseAuth] bootstrapAuth finished')
            }
        };

        bootstrapAuth();

        const { data: authListener } = supabase.auth.onAuthStateChange(async (event: AuthChangeEvent, newSession: Session | null) => {
            if (!isMountedRef.current) return;

            console.log('[SupabaseAuth] onAuthStateChange:', { event, hasSession: !!newSession, bootstrapCompleted: bootstrapCompletedRef.current });

            // すべてのイベントを処理（冗長性のため）
            console.log('[SupabaseAuth] Processing all auth events for redundancy');

            setSession(newSession);
            const newAuthUser = newSession?.user ?? null;

            if (event === 'SIGNED_IN' || event === 'USER_UPDATED' || event === 'INITIAL_SESSION') {
                if (newAuthUser && newSession) {
                    console.log('[SupabaseAuth] User signed in/updated/initial:', newAuthUser.id);

                    // セッション有効期限チェック（バッファ時間を追加）
                    const now = Math.floor(Date.now() / 1000);
                    const bufferTime = 300; // 5分のバッファ時間
                    if (newSession.expires_at && newSession.expires_at < (now + bufferTime) && !isRefreshingRef.current) {
                        console.log('[SupabaseAuth] Session will expire soon, attempting refresh...');
                        isRefreshingRef.current = true;
                        try {
                            const { data: refreshData, error: refreshError } = await supabase.auth.refreshSession();
                            if (refreshError || !refreshData.session) {
                                console.log('[SupabaseAuth] Refresh failed in onAuthStateChange, signing out');
                                setSession(null);
                                setUser(null);
                                // setIsAdmin(false); // fetchProfileで設定されるまで変更しない
                                setIsLoading(false);
                                supabase.auth.signOut();
                                return;
                            }
                            console.log('[SupabaseAuth] Session refreshed successfully in onAuthStateChange');
                            // リフレッシュされたセッションを使用
                            const refreshedSession = refreshData.session;
                            setSession(refreshedSession);
                        } catch (refreshError) {
                            console.error('[SupabaseAuth] Refresh error in onAuthStateChange:', refreshError);
                            setSession(null);
                            setUser(null);
                            // setIsAdmin(false); // fetchProfileで設定されるまで変更しない
                            setIsLoading(false);
                            supabase.auth.signOut();
                            return;
                        } finally {
                            isRefreshingRef.current = false;
                        }
                    }

                    // 既存のユーザー情報をチェック（userRefを使用して最新の状態を取得）
                    const currentUser = userRef.current;
                    if (!currentUser || currentUser.id !== newAuthUser.id) {
                        // 初回ログイン時または異なるユーザーの場合のみ基本ユーザー情報を設定
                        const baseUser: AppUser = {
                            ...(newAuthUser as SupabaseUser),
                            email: newAuthUser.email || '',
                            username: newAuthUser.email?.split('@')[0] || null,
                            name: newAuthUser.user_metadata?.name || newAuthUser.email || null,
                            role: 'user' as UserRole, // デフォルトロール（fetchProfileで更新される）
                            is_active: true,
                            craftsman_id: null,
                        };
                        setUser(baseUser);
                        logStateChange('setUser (onAuthStateChange)', { userId: baseUser.id, role: baseUser.role });
                        console.log('[SupabaseAuth] New user set via onAuthStateChange:', { userId: newAuthUser.id });
                    } else {
                        // 既存のユーザー情報がある場合は保持（上書きしない）
                        console.log('[SupabaseAuth] Existing user info preserved:', { userId: currentUser.id, role: currentUser.role });
                    }
                    // isAdminはfetchProfileで設定されるまで変更しない
                    // setIsAdmin(false);
                    // logStateChange('setIsAdmin (onAuthStateChange)', false);
                    // ロールが確定するまでローディング状態を維持
                    setIsLoading(false); // ローディング状態を終了
                    logStateChange('setIsLoading (onAuthStateChange)', false);

                    // バックグラウンドでプロフィール取得（詳細情報で更新）
                    fetchProfile(newAuthUser.id, newSession).catch(error => {
                        console.error('[SupabaseAuth] Background profile fetch failed in onAuthStateChange:', error);
                    });
                } else {
                    console.log('[SupabaseAuth] No user in signed in event');
                    setUser(null);
                    // setIsAdmin(false); // fetchProfileで設定されるまで変更しない
                    setIsLoading(false);
                }
            } else if (event === 'SIGNED_OUT') {
                console.log('[SupabaseAuth] User signed out');
                setUser(null);
                setSession(null);
                // テスト用: 一時的にコメントアウト
                // setIsAdmin(false); // ログアウト時は確実にfalseに設定
                setIsLoading(false);
            } else if (event === 'TOKEN_REFRESHED' && newAuthUser) {
                console.log('[SupabaseAuth] Token refreshed for user:', newAuthUser.id);
                // トークンリフレッシュ時は既存のユーザー情報を維持
                setUser(prevUser => {
                    if (prevUser) {
                        return {
                            ...prevUser,
                            ...(newAuthUser as SupabaseUser),
                            // プロファイル固有のフィールドは既存のものを保持
                            role: prevUser.role,
                            username: prevUser.username,
                            name: prevUser.name,
                            is_active: prevUser.is_active,
                        };
                    }
                    return prevUser;
                });
                // isAdminは既存の値を維持（変更しない）
            }
        });


        return () => {
            isMountedRef.current = false;
            authListener?.subscription?.unsubscribe();
        };
    }, [supabase, fetchProfile]);

    const signOut = useCallback(async () => {
        setIsLoading(true)
        await supabase.auth.signOut()
    }, [supabase])

    const value = useMemo(() => ({
        supabase,
        session,
        user,
        isAdmin,
        isLoading,
        signOut,
    }), [supabase, session, user, isAdmin, isLoading, signOut])

    return (
        <SupabaseAuthContext.Provider value={value}>
            {children}
        </SupabaseAuthContext.Provider>
    )
}

/**
 * Supabase認証フック
 */
export const useSupabaseAuth = (): SupabaseAuthContextType => {
    const context = useContext(SupabaseAuthContext)
    if (context === undefined) {
        throw new Error('useSupabaseAuth must be used within a SupabaseAuthProvider')
    }
    return context
}

/**
 * 認証フックのエイリアス（互換性のため）
 */
export const useAuth = useSupabaseAuth 